import { mkConfig, generateCsv, asString } from 'export-to-csv';

export interface CsvParseResult {
	success: object[];
	failed: string[][];
}

export class CsvUtil {
	static generateCsv(
		rows: {
			[k: string]: number | string | boolean | null | undefined;
			[k: number]: number | string | boolean | null | undefined;
		}[]
	): string {
		const csvConfig = mkConfig({ useKeysAsHeaders: true });
		const csvOutput = generateCsv(csvConfig)(rows);
		return asString(csvOutput);
	}

	static async convertStringToCsv(data: string): Promise<CsvParseResult> {
		if (!data.trim()) {
			return { success: [], failed: [] };
		}

		// Normalize line endings and split into lines
		const lines = data
			.replace(/\r\n|\r/g, '\n') // normalize to \n
			.split('\n')
			.map(line => line.trim())
			.filter(line => line.length > 0); // remove empty lines

		if (lines.length < 1) {
			return { success: [], failed: [] };
		}

		// Parse header row
		const headers = this.parseCsvLine(lines[0]);

		const success: object[] = [];
		const failed: string[][] = [];

		// Start from second line (data rows)
		for (let i = 1; i < lines.length; i++) {
			const line = lines[i];
			const values = this.parseCsvLine(line);

			if (values.length !== headers.length) {
				console.warn(
					`Skipping row ${i + 1} — column count mismatch. ` +
						`Expected ${headers.length}, got ${values.length}. Line: "${line.slice(0, 80)}${line.length > 80 ? '...' : ''}"`
				);
				failed.push(values); // ← store the parsed fields as array of strings
				continue;
			}

			// Build object using headers as keys
			const obj: Record<string, string> = {};

			for (let j = 0; j < headers.length; j++) {
				const key = headers[j].trim();
				obj[key] = values[j];
			}

			success.push(obj);
		}

		return { success, failed };
	}

	/**
	 * Parse a single CSV line respecting quoted fields
	 */
	private static parseCsvLine(line: string): string[] {
		const result: string[] = [];
		let current = '';
		let inQuotes = false;
		let i = 0;

		while (i < line.length) {
			const char = line[i];

			if (char === '"' && !inQuotes) {
				// Start of quoted field
				inQuotes = true;
				i++;
				continue;
			}

			if (char === '"' && inQuotes) {
				if (i + 1 < line.length && line[i + 1] === '"') {
					// Escaped quote inside quoted field
					current += '"';
					i += 2;
					continue;
				} else {
					// End of quoted field
					inQuotes = false;
					i++;
					continue;
				}
			}

			if (char === ',' && !inQuotes) {
				// End of field
				result.push(current);
				current = '';
				i++;
				continue;
			}

			// Normal character
			current += char;
			i++;
		}

		// Don't forget the last field
		result.push(current);

		// Clean up: trim unquoted fields (quoted fields keep their internal spaces)
		return result.map((field, idx) => {
			// If the original line segment wasn't quoted, trim it
			// This is a simplification — more precise would require tracking quote status per field
			return field.trim();
		});
	}
}
